/**
 * The visit — what `sessionKey` means on the wire, decided here.
 *
 * The server counts a session as a distinct `(user_id, session_key)` pair and
 * sums `duration_ms` within it, so this key IS the unit "how many times did
 * they come, and how long did they stay" is measured in. Getting its lifetime
 * wrong does not corrupt one number; it corrupts the lead score.
 *
 * ONE VISIT IS ONE TAB, ACROSS RELOADS. `sessionStorage` is exactly that
 * lifetime — it survives a refresh and a same-tab navigation, and a new tab or
 * a new window starts fresh. The alternatives are both wrong in ways that
 * matter:
 *
 *  - A module variable would make every reload a new visit, so a learner who
 *    refreshes twice looks like three sittings.
 *  - `localStorage` would make every tab share one, so two tabs open on two
 *    lectures would collapse into one sitting whose duration is the sum of
 *    both — time they were never spending twice.
 *
 * ONE VISIT IS ALSO ONE IDENTITY — and the record holds ONE KEY PER IDENTITY,
 * not one key plus the identity it happens to belong to. Signing out and back
 * in was already handled by `endVisit`; switching membership in the picker
 * never unmounts anything, so without the identity in the record two of them
 * would file their events under one sitting.
 *
 * Keeping every identity's key rather than the latest one buys two things. A
 * learner who switches away and back is still on their first visit to that
 * institution, which is what a tab is; and — the reason it is written this way
 * — the key is read out of storage while a beacon is being CONSTRUCTED, which
 * happens during render. React is entitled to begin a render for an identity
 * and then throw it away, and a single-slot record would have let that
 * discarded render overwrite the committed identity's key. The next reload
 * would then have minted a second one and split one sitting into two sessions
 * on the administrator's screen.
 *
 * IT IS NOT AN IDENTIFIER OF A PERSON and must never become one. The server
 * already knows who is asking from the bearer token; this only groups their
 * events. It is deliberately regenerated on every new tab, never sent
 * anywhere else, and cleared on sign-out along with everything else in the
 * tab's storage.
 */

import { demoActive, demoStorageKey } from '@cusp/api-client/demo';
const storageKey = () => (demoActive() ? demoStorageKey('telemetry-visit') : 'cusp.visit');

/** A short opaque id. `crypto.randomUUID` where it exists; a counter otherwise. */
function newKey(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // jsdom without webcrypto, and very old browsers. Uniqueness only has to
  // hold within one tab, and `clientEventId` carries the within-visit
  // uniqueness, so a timestamp plus a random tail is sufficient here.
  return `v${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Visit keys by identity scope. Bounded: nobody holds eight memberships. */
type VisitRecord = Record<string, string>;

const MAX_SCOPES = 8;

function readRecord(): VisitRecord {
  const raw = window.sessionStorage.getItem(storageKey());
  if (raw === null) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: VisitRecord = {};
    for (const [scope, key] of Object.entries(parsed)) {
      if (typeof key === 'string') out[scope] = key;
    }
    return out;
  } catch {
    // A record written by an older build, or by something else entirely.
    return {};
  }
}

/**
 * The current visit's key for one identity, creating one on first use.
 *
 * Falls back to a per-load key when storage is unavailable — Safari's private
 * mode has historically thrown on `sessionStorage` access rather than
 * returning null, and telemetry must never be the thing that breaks a lecture.
 */
export function visitKey(scope: string): string {
  try {
    const record = readRecord();
    const existing = record[scope];
    if (existing !== undefined) return existing;
    const created = newKey();
    /*
     * Written oldest first, and the new one last, so that the cap takes the
     * OLDEST out. Built the other way round it read newest-first and evicted
     * the identity the learner had just been using — which, on the ninth
     * switch, split the sitting they were in the middle of.
     */
    const entries = Object.entries(record);
    const kept = entries.slice(Math.max(0, entries.length + 1 - MAX_SCOPES));
    const next: VisitRecord = {};
    for (const [s, key] of kept) next[s] = key;
    next[scope] = created;
    window.sessionStorage.setItem(storageKey(), JSON.stringify(next));
    return created;
  } catch {
    fallback[scope] ??= newKey();
    return fallback[scope];
  }
}

let fallback: VisitRecord = {};

/** Forget the visit — a new one begins on the next event. Used on sign-out. */
export function endVisit(): void {
  fallback = {};
  try {
    window.sessionStorage.removeItem(storageKey());
  } catch {
    // Nothing to clear if storage was never reachable.
  }
}

/**
 * Ids unique WITHIN a visit, which is all the contract asks for — ingest is
 * idempotent on `(session, clientEventId)`, so a retried beacon must reuse the
 * id it was assigned when the event happened, never a fresh one.
 *
 * A counter rather than a random id, because the failure it guards against is
 * a retry double-counting someone's time on site, and a counter makes that
 * impossible to get wrong by construction: the id is assigned once, at the
 * moment the event is queued, and travels with it.
 *
 * BUT THE COUNTER IS PER PAGE LOAD AND THE VISIT IS NOT, which is why the id
 * carries a per-load tag as well. A visit survives a reload by design; the
 * counter cannot, because nothing in a fresh JavaScript context remembers it.
 * A bare counter therefore restarted at `e1` after every refresh and re-used
 * ids the first load had already spent — and ingest, doing exactly what it
 * promises, recognised the whole of the second load as duplicates and stored
 * none of it. A learner who refreshed once reported nothing for the rest of
 * their visit.
 */
const loadTag = Math.random().toString(36).slice(2, 10);
let seq = 0;

export function nextEventId(): string {
  seq += 1;
  return `${loadTag}-${seq}`;
}

/** Test seam: reset the counter between cases. */
export function resetEventIds(): void {
  seq = 0;
}
